import React, { FC } from 'react';

import Card, { CardLink } from '@rover/components/src/cards/Card';
import StaticFeaturedReview from '@rover/components/src/cards/CarouselSitterCard/components/StaticFeaturedReview';
import LegacyPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/LegacyPriceColumn';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacity } from '@rover/kibble/utils';
import { HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import useDeviceContext from '@rover/react-lib/src/context/useDeviceContext';
import {
  type GroomingSearchCardSection,
  GroomingSearchCardClick,
} from '@rover/react-lib/src/pages/grooming/analyticsEvents';
import GroomingAvatarColumn from '@rover/react-lib/src/pages/grooming/GroomingSearchPage/components/GroomingSearchResultsList/GroomingSearchCard/GroomingAvatarColumn';
import GroomingDetailsSection from '@rover/react-lib/src/pages/grooming/GroomingSearchPage/components/GroomingSearchResultsList/GroomingSearchCard/GroomingDetailsSection';
import GroomingInfoColumn from '@rover/react-lib/src/pages/grooming/GroomingSearchPage/components/GroomingSearchResultsList/GroomingSearchCard/GroomingInfoColumn';
import { ApiGroomingSearchRetrieveGroomingType } from '@rover/rsdk/src/apiClient/latest/model/apiGroomingSearchRetrieveGroomingType';
import type { GroomingSearchResult } from '@rover/rsdk/src/apiClient/latest/model/groomingSearchResult';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

export const CARD_WIDTH = [300, 350];

const UNIT_LABELS: Record<ApiGroomingSearchRetrieveGroomingType, string> = {
  [ApiGroomingSearchRetrieveGroomingType.full_groom]: 'full groom',
  [ApiGroomingSearchRetrieveGroomingType.bath_and_brush]: 'bath & brush',
};

type Props = {
  searchResult: GroomingSearchResult;
  index: number;
  section: GroomingSearchCardSection;
  currencyCode?: string;
  locale?: Locale;
};

/**
 * A groomer as the SEO location carousel shows them: the same shell CarouselSitterCard uses, with
 * the grooming rows inside. GroomingSearchCard is not reused, because its wrapper is styled for the
 * search page's stacked list — a bottom border between siblings and a hover bar that pairs the row
 * with the map.
 */
const GroomingCarouselCard: FC<Props> = ({
  searchResult,
  index,
  section,
  currencyCode = 'USD',
  locale = Locale.EnUs,
}) => {
  const { colors } = useTheme();
  const { isDesktop } = useDeviceContext();

  const {
    defaultImage,
    shortName,
    primaryBadgeData,
    ratingsAverage,
    reviewsCount,
    distanceFromSearchCenterObj,
    groomerWebUrl,
    yearsOfExperience,
    isHosting,
    travelsToOwner,
    displayedGroomingType,
    displayedPrice,
    repeatClientCount,
    supportedPetSizes,
    reviewText,
    reviewDogPhotoUrl,
    reviewerShortName,
  } = searchResult;

  const resultNumber = index + 1;
  const unitAsSentence =
    UNIT_LABELS[displayedGroomingType as ApiGroomingSearchRetrieveGroomingType];

  const handleClick = (): void => {
    emitAnalyticsEvent(new GroomingSearchCardClick({ section }));
  };

  return (
    <Flex
      data-testid="grooming-carousel-card"
      maxWidth={CARD_WIDTH}
      minWidth={CARD_WIDTH}
      width={CARD_WIDTH}
      sx={{ boxShadow: `0px 1px 4px 0px ${withOpacity(colors.neutral['900'], 0.23)}` }}
      borderRadius="8px"
      position="relative"
      flexDirection="column"
      height="100%"
    >
      <CardLink
        aria-label={`Search result number ${resultNumber}: ${shortName}`}
        href={groomerWebUrl}
        rel="nofollow"
        target={isDesktop ? '_blank' : '_self'}
        onClick={handleClick}
      />
      <Card borderRadius="8px" background="primary" cardStyle="inset" isStacked padding="4x">
        <Flex flexDirection="column" gap="3x">
          <HorizontalLayout noWrap>
            <GroomingAvatarColumn
              imageUrl={defaultImage.small}
              shortName={shortName}
              badge={primaryBadgeData ?? undefined}
            />
            <GroomingInfoColumn
              resultNumber={resultNumber}
              shortName={shortName}
              distanceDisplay={distanceFromSearchCenterObj.distanceDisplay}
            />
            <LegacyPriceColumn
              currencyCode={currencyCode}
              locale={locale}
              price={displayedPrice}
              unitAsSentence={unitAsSentence}
            />
          </HorizontalLayout>
          <GroomingDetailsSection
            ratingsAverage={ratingsAverage}
            reviewsCount={reviewsCount}
            repeatClientCount={repeatClientCount}
            yearsOfExperience={yearsOfExperience}
            isHosting={isHosting}
            travelsToOwner={travelsToOwner}
            supportedPetSizes={supportedPetSizes}
          />
        </Flex>
      </Card>
      {reviewText && (
        <Card
          borderRadius="8px"
          cardStyle="inset"
          isStacked
          paddingX="5x"
          paddingY="4x"
          background="secondary"
          height="100%"
        >
          <StaticFeaturedReview
            reviewText={reviewText}
            reviewPhotoUrl={reviewDogPhotoUrl ?? null}
            reviewerShortName={reviewerShortName ?? ''}
            description=""
            defaultAvatar="dog"
          />
        </Card>
      )}
    </Flex>
  );
};

export default GroomingCarouselCard;
